import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  SectionList,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import { useRouter } from 'expo-router';
import { showAlert, showConfirm } from '../../../src/utils/alert';
import { activitiesApi, recurrenceSchedulesApi, Activity } from '../../../src/api/client';
import { entityMutation } from '../../../src/api/queryClient';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { useTeamStore } from '../../../src/stores/teamStore';
import { VirtualActivity, isVirtualActivity, expandAllSchedules, mergeRealAndVirtual, getThisWeekRange, getUpcomingRange } from '../../../src/utils/virtualExpansion';
import { decrypt, decryptEntityTitle, getKeyForTeam } from '../../../src/utils/encryption';
import { ACTIVITY_STATUS_COLORS } from '../../../src/theme';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import { formatMinutes, formatWeekdayDate, isToday, isPast, isTomorrow, isThisWeekRemaining, isAfterThisWeek } from '../../../src/utils/format';
import { POLL_INTERVAL_MS } from '../../../src/constants';
import { deriveActivityStatus } from '../../../src/utils/displayStatus';
import { useRefetchOnFocus } from '../../../src/hooks/useRefetchOnFocus';
import FloatingActionButton from '../../../src/components/FloatingActionButton';
import UndoBanner from '../../../src/components/UndoBanner';
import DataStateView from '../../../src/components/DataStateView';
import LastUpdatedBanner from '../../../src/components/LastUpdatedBanner';
import FilterTabs from '../../../src/components/FilterTabs';
import Icon, { ICON_SIZES } from '../../../src/components/Icon';
import { useActivitiesList } from '../../../src/hooks/queries/useActivitiesList';
import { staysVisible } from '../../../src/utils/activityVisibility';

const FILTER_TABS = [
  { key: 'today', label: 'This Week' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'done', label: 'Done' },
] as const;

type FilterKey = typeof FILTER_TABS[number]['key'];

type AnyActivity = Activity | VirtualActivity;

interface ActivitySection {
  title: string;
  data: AnyActivity[];
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  listContent: { padding: 16, paddingBottom: 80 },
  sectionHeader: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const, paddingVertical: 8, marginBottom: 4, marginTop: 8 },
  sectionHeaderText: { fontSize: 14, fontWeight: '700' as const, color: theme.colors.textMuted, textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  sectionCount: { fontSize: 12, fontWeight: '600' as const, color: theme.colors.textDisabled, backgroundColor: theme.colors.bgTertiary, borderRadius: 10, minWidth: 22, height: 22, textAlign: 'center' as const, lineHeight: 22, paddingHorizontal: 6, overflow: 'hidden' as const },
  activityCard: { flexDirection: 'row' as const, alignItems: 'flex-start' as const, backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, marginBottom: 10, ...theme.shadows.sm, gap: 12 },
  checkbox: { width: 24, height: 24, borderRadius: 12, borderWidth: 2, borderColor: theme.colors.borderMedium, alignItems: 'center' as const, justifyContent: 'center' as const, marginTop: 2 },
  checkboxDone: { backgroundColor: theme.colors.primary, borderColor: theme.colors.primary },
  checkmark: { fontSize: 14, color: theme.colors.background, fontWeight: '700' as const },
  activityContent: { flex: 1 },
  activityTitle: { fontSize: 16, fontWeight: '600' as const, color: theme.colors.textPrimary, lineHeight: 22, marginBottom: 8 },
  activityTitleDone: { textDecorationLine: 'line-through' as const, color: theme.colors.textDisabled },
  activityMeta: { flexDirection: 'row' as const, alignItems: 'center' as const, flexWrap: 'wrap' as const, gap: 8 },
  statusBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 },
  statusText: { fontSize: 12, fontWeight: '700' as const, textTransform: 'uppercase' as const },
  estimateText: { fontSize: 13, color: theme.colors.textMuted, fontWeight: '500' as const },
  dateText: { fontSize: 13, color: theme.colors.textMuted, fontWeight: '500' as const },
  goalLabel: { fontSize: 12, color: theme.colors.textMuted, fontWeight: '500' as const, maxWidth: 150 },
  emptyContainer: { flexGrow: 1, justifyContent: 'center' as const, alignItems: 'center' as const },
  emptyState: { alignItems: 'center' as const, paddingHorizontal: 40 },
  emptyIcon: { fontSize: 48, marginBottom: 16, color: theme.colors.borderMedium },
  emptyTitle: { fontSize: 20, fontWeight: '700' as const, color: theme.colors.textSecondary, marginBottom: 8 },
  emptySubtitle: { fontSize: 15, color: theme.colors.textDisabled, textAlign: 'center' as const, lineHeight: 22 },
});

export default function ActivityScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  const { data, isLoading, error, refetch, isRefetching, dataUpdatedAt, isStale } = useActivitiesList();
  useRefetchOnFocus(refetch);

  // The clock the derived statuses are evaluated against. Ticking it re-renders
  // the list as activities cross their start times; nothing is written.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const [activeFilter, setActiveFilter] = useState<FilterKey>('today');
  // The last status change that took an activity off screen, while it can
  // still be undone. `id` is the real row, `previous` the status to restore.
  const [undo, setUndo] = useState<{ message: string; id: string; previous: string } | null>(null);
  const dismissUndo = useCallback(() => setUndo(null), []);
  const [activityTitles, setActivityTitles] = useState<Record<string, string>>({});
  const [activityEstimates, setActivityEstimates] = useState<Record<string, number | null>>({});
  const [goalMap, setGoalMap] = useState<Record<string, string>>({});
  const [upcomingWeeks, setUpcomingWeeks] = useState(4);

  // The server is the only source of truth for what exists and what is done.
  // todo/in-progress is a function of start_time vs now, derived on the way in.
  const activities = useMemo(() => {
    if (!data) return [] as Activity[];
    return data.activities.map((a) => {
      const status = deriveActivityStatus(a, now);
      return status === a.status ? a : { ...a, status };
    });
  }, [data?.activities, now]);

  const schedules = data?.schedules ?? [];
  const templateActivities = data?.templateActivities ?? [];

  // Decrypt activity titles and estimates when data changes
  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    (async () => {
      const allActivitiesToDecrypt = [...data.activities, ...data.templateActivities];
      const [titleEntries, estimateEntries] = await Promise.all([
        Promise.all(
          allActivitiesToDecrypt.map(async (t) => [t.id, await decryptEntityTitle(t)] as const)
        ),
        Promise.all(
          allActivitiesToDecrypt.map(async (t): Promise<[string, number | null]> => {
            if (t.estimated_minutes_encrypted && t.encryption_metadata?.estimated_minutes_iv) {
              try {
                const key = await getKeyForTeam(t.team_id);
                const plainStr = await decrypt(t.estimated_minutes_encrypted, { iv: t.encryption_metadata.estimated_minutes_iv }, key);
                return [t.id, parseInt(plainStr, 10)];
              } catch { return [t.id, null]; }
            }
            return [t.id, null];
          })
        ),
      ]);
      if (!cancelled) {
        setActivityTitles(Object.fromEntries(titleEntries));
        setActivityEstimates(Object.fromEntries(estimateEntries));
      }
    })();
    return () => { cancelled = true; };
  }, [data?.activities, data?.templateActivities]);

  // Decrypt goal titles when data changes
  useEffect(() => {
    if (!data?.goals) return;
    let cancelled = false;
    (async () => {
      const goalEntries = await Promise.all(
        data.goals.map(async (g) => [g.id, await decryptEntityTitle(g)] as const)
      );
      if (!cancelled) setGoalMap(Object.fromEntries(goalEntries));
    })();
    return () => { cancelled = true; };
  }, [data?.goals]);

  const notDone = (t: AnyActivity) => t.status !== 'done' && t.status !== 'skipped';

  // Expand virtual occurrences from recurrence schedules
  const allActivities = useMemo((): AnyActivity[] => {
    const activitiesWithTemplates = [...activities, ...templateActivities];

    const thisWeekRange = getThisWeekRange();
    const thisWeekVirtuals = expandAllSchedules(schedules, activitiesWithTemplates, thisWeekRange.start, thisWeekRange.end);

    const upcomingRange = getUpcomingRange(upcomingWeeks);
    const upcomingVirtuals = expandAllSchedules(schedules, activitiesWithTemplates, upcomingRange.start, upcomingRange.end);

    const thisWeekIds = new Set(thisWeekVirtuals.map((v) => v.id));
    const uniqueUpcoming = upcomingVirtuals.filter((v) => !thisWeekIds.has(v.id));

    const allVirtuals = [...thisWeekVirtuals, ...uniqueUpcoming];

    // Copy decrypted titles/estimates from template to virtual activities
    for (const v of allVirtuals) {
      if (isVirtualActivity(v)) {
        const schedule = schedules.find((s) => s.id === v._scheduleId);
        if (schedule?.activity_template_id) {
          const templateTitle = activityTitles[schedule.activity_template_id];
          if (templateTitle) activityTitles[v.id] = templateTitle;
          const templateEstimate = activityEstimates[schedule.activity_template_id];
          if (templateEstimate !== undefined) activityEstimates[v.id] = templateEstimate;
        }
      }
    }

    return mergeRealAndVirtual(activities, allVirtuals) as AnyActivity[];
  }, [activities, schedules, templateActivities, upcomingWeeks, activityTitles, activityEstimates]);

  const sections = useMemo((): ActivitySection[] => {
    const sortByStartTime = (a: AnyActivity, b: AnyActivity) =>
      (a.start_time || '').localeCompare(b.start_time || '');

    switch (activeFilter) {
      case 'today': {
        const pending = allActivities.filter((t) => isPast(t.start_time) && notDone(t)).sort(sortByStartTime);
        const today = allActivities.filter((t) => isToday(t.start_time)).sort(sortByStartTime);
        const tomorrow = allActivities.filter((t) => isTomorrow(t.start_time) && notDone(t)).sort(sortByStartTime);
        const thisWeek = allActivities.filter((t) => isThisWeekRemaining(t.start_time) && notDone(t)).sort(sortByStartTime);
        const result: ActivitySection[] = [];
        if (pending.length > 0) result.push({ title: 'Pending', data: pending });
        if (today.length > 0) result.push({ title: 'Today', data: today });
        if (tomorrow.length > 0) result.push({ title: 'Tomorrow', data: tomorrow });
        if (thisWeek.length > 0) result.push({ title: 'This Week', data: thisWeek });
        return result;
      }
      case 'upcoming': {
        const items = allActivities.filter((t) => isAfterThisWeek(t.start_time) && notDone(t)).sort(sortByStartTime);
        const groups = new Map<string, AnyActivity[]>();
        for (const item of items) {
          const d = new Date(item.start_time);
          const key = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          const group = groups.get(key);
          if (group) group.push(item);
          else groups.set(key, [item]);
        }
        return Array.from(groups, ([title, data]) => ({ title, data }));
      }
      case 'done': {
        const items = allActivities.filter((t) => t.status === 'done' || t.status === 'skipped').sort(sortByStartTime);
        return items.length > 0 ? [{ title: '', data: items }] : [];
      }
      default:
        return [];
    }
  }, [allActivities, activeFilter]);

  const filterCounts = useMemo(() => {
    const pendingCount = allActivities.filter((t) => isPast(t.start_time) && notDone(t)).length;
    const todayCount = allActivities.filter((t) => isToday(t.start_time)).length;
    const tomorrowCount = allActivities.filter((t) => isTomorrow(t.start_time) && notDone(t)).length;
    const thisWeekCount = allActivities.filter((t) => isThisWeekRemaining(t.start_time) && notDone(t)).length;
    return {
      today: pendingCount + todayCount + tomorrowCount + thisWeekCount,
      upcoming: allActivities.filter((t) => isAfterThisWeek(t.start_time) && notDone(t)).length,
      done: allActivities.filter((t) => t.status === 'done' || t.status === 'skipped').length,
    };
  }, [allActivities]);

  /**
   * Set an activity's status. Returns the id of the real row that now holds
   * it, which differs from `activity.id` for a recurring occurrence — those
   * are materialized into a new row on first write — or null on failure.
   */
  const applyStatus = async (activity: AnyActivity, status: string): Promise<string | null> => {
    if (!requireConnectivity()) return null;
    try {
      if (isVirtualActivity(activity)) {
        const res = await entityMutation(() =>
          recurrenceSchedulesApi.materialize(activity._scheduleId, {
            occurrence_time: activity._occurrenceTime,
            status,
          }),
        );
        // entityMutation has already refetched, so the materialized row is in
        // the list; its title is still decrypting, so seed it from the virtual's.
        const materialized = res.data;
        setActivityTitles((prev) => ({ ...prev, [materialized.id]: prev[activity.id] || 'Untitled' }));
        setActivityEstimates((prev) => ({ ...prev, [materialized.id]: prev[activity.id] ?? null }));
        return materialized.id;
      }
      await entityMutation(() => activitiesApi.updateStatus(activity.id, status));
      return activity.id;
    } catch {
      showAlert('Error', `Failed to ${status === 'skipped' ? 'skip' : 'update'} activity.`);
      return null;
    }
  };

  // No confirmation: the change is one tap to reverse. Where the activity stays
  // on screen its own checkbox is that tap; where it leaves (an overdue or
  // later-this-week activity, or un-marking from Done), the banner stands in
  // for the control that just disappeared.
  const setStatus = async (activity: AnyActivity, status: string, message: string) => {
    const previous = activity.status;
    const id = await applyStatus(activity, status);
    if (id && !staysVisible(activeFilter, activity.start_time, status)) {
      setUndo({ message, id, previous });
    }
  };

  const handleToggleStatus = (activity: AnyActivity) => {
    const finished = activity.status === 'done' || activity.status === 'skipped';
    void setStatus(activity, finished ? 'todo' : 'done', finished ? 'Moved back to to-do' : 'Marked done');
  };

  const handleSkip = (activity: AnyActivity) => {
    if (activity.status === 'done' || activity.status === 'skipped') return;
    showConfirm(
      'Skip Activity',
      `Skip "${activityTitles[activity.id] || 'Untitled'}"?`,
      () => void setStatus(activity, 'skipped', 'Skipped'),
      { confirmText: 'Skip', destructive: true },
    );
  };

  const handleUndo = async () => {
    if (!undo) return;
    const { id, previous } = undo;
    setUndo(null);
    if (!requireConnectivity()) return;
    try {
      // Always a real row by now: materializing is what the original change did.
      await entityMutation(() => activitiesApi.updateStatus(id, previous));
    } catch {
      showAlert('Error', 'Failed to undo.');
    }
  };

  const renderActivity = ({ item }: { item: AnyActivity }) => {
    const statusStyle = ACTIVITY_STATUS_COLORS[item.status] || ACTIVITY_STATUS_COLORS.todo;
    const isDone = item.status === 'done' || item.status === 'skipped';
    const goalName = item.goal_id ? goalMap[item.goal_id] : null;
    const isRecurring = !!item.recurrence_schedule_id || isVirtualActivity(item);

    return (
      <View style={styles.activityCard}>
        <TouchableOpacity
          style={[styles.checkbox, isDone && styles.checkboxDone]}
          onPress={() => handleToggleStatus(item)}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityState={{ checked: isDone }}
          accessibilityLabel={`Mark ${activityTitles[item.id] || 'Untitled'} as ${isDone ? 'not done' : 'done'}`}
          testID={`activities.activity-checkbox-${item.id}`}
        >
          {isDone && <Text style={styles.checkmark} accessible={false}>{'\u2713'}</Text>}
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.activityContent}
          onPress={() => {
            if (isVirtualActivity(item)) {
              router.push({ pathname: `/(tabs)/activities/${item.id}` as any, params: { scheduleId: item._scheduleId, occurrenceTime: item._occurrenceTime } });
            } else {
              router.push(`/(tabs)/activities/${item.id}` as any);
            }
          }}
          onLongPress={() => handleSkip(item)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={`${activityTitles[item.id] || 'Untitled'}, status ${item.status}${isRecurring ? ', recurring' : ''}`}
          testID={`activities.activity-card-${item.id}`}
        >
          <Text
            style={[styles.activityTitle, isDone && styles.activityTitleDone]}
            numberOfLines={2}
            accessible={false}
          >
            {activityTitles[item.id] || 'Untitled'}
            {isRecurring && <> <Icon name="repeat-outline" size={ICON_SIZES.inline} /></>}
          </Text>
          <View style={styles.activityMeta} accessible={false}>
            {item.start_time && (
              <Text style={styles.dateText} accessible={false}>
                {formatWeekdayDate(item.start_time)}
              </Text>
            )}
            <View style={[styles.statusBadge, { backgroundColor: statusStyle.bg }]} accessible={false}>
              <Text style={[styles.statusText, { color: statusStyle.text }]} accessible={false}>
                {item.status}
              </Text>
            </View>
            {activityEstimates[item.id] !== null && activityEstimates[item.id] !== undefined && (
              <Text style={styles.estimateText} accessible={false}>
                {formatMinutes(activityEstimates[item.id]!)}
              </Text>
            )}
            {goalName && (
              <Text style={styles.goalLabel} numberOfLines={1} accessible={false}>
                {'\u{1F3AF}'} {goalName}
              </Text>
            )}
          </View>
        </TouchableOpacity>
      </View>
    );
  };

  const renderSectionHeader = ({ section }: { section: ActivitySection }) => {
    if (!section.title) return null;
    return (
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionHeaderText}>{section.title}</Text>
        <Text style={styles.sectionCount}>{section.data.length}</Text>
      </View>
    );
  };

  return (
    <DataStateView
      isLoading={isLoading}
      error={error}
      hasData={!!data}
      onRetry={refetch}
      entityName="activities"
      testIDPrefix="activities"
    >
    <View style={styles.container}>
      <LastUpdatedBanner dataUpdatedAt={dataUpdatedAt} isStale={isStale} />
      {/* Filter Tabs */}
      <FilterTabs
        tabs={FILTER_TABS as unknown as Array<{ key: string; label: string }>}
        activeKey={activeFilter}
        onSelect={(key) => setActiveFilter(key as FilterKey)}
        counts={filterCounts}
        testIDPrefix="activities"
      />

      <SectionList
        sections={sections}
        keyExtractor={(item) => item.id}
        renderItem={renderActivity}
        renderSectionHeader={renderSectionHeader}
        contentContainerStyle={sections.length === 0 ? styles.emptyContainer : styles.listContent}
        stickySectionHeadersEnabled={false}
        onEndReached={activeFilter === 'upcoming' ? () => setUpcomingWeeks((w) => w + 4) : undefined}
        onEndReachedThreshold={0.5}
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={() => refetch()}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Text style={styles.emptyIcon}>{'\u2611'}</Text>
            <Text style={styles.emptyTitle}>
              {activeFilter === 'today' ? 'No activities this week' : `No ${activeFilter} activities`}
            </Text>
            <Text style={styles.emptySubtitle}>
              {activeFilter === 'today'
                ? 'Tap the + button to create an activity'
                : 'Activities matching this filter will appear here'}
            </Text>
          </View>
        }
      />
      <FloatingActionButton onPress={() => router.push('/(tabs)/activities/create')} accessibilityLabel="Create new activity" />
      {undo && (
        <UndoBanner key={undo.id} message={undo.message} onUndo={handleUndo} onDismiss={dismissUndo} />
      )}
    </View>
    </DataStateView>
  );
}
